import { line as d3Line } from 'd3-shape';

import {
  DEFAULT_DATA_POINT_RADIUS,
  DEFAULT_FILL_OPACITY,
  DEFAULT_SERIES_CURVE_SHAPE,
} from '../../../../constants.js';
import { OVERLAY_PADDING } from '../../../data-point/constants.js';
import {
  FnCanvasAccessor,
  LineCanvasInstance,
} from '../../types/canvas-shape.js';
import {
  ARC_BATCH_SIZE,
  PATH_BATCH_SIZE,
  createBatchedStrokeContext,
} from '../batched-path-context.js';
import { getCanvasColor } from '../canvas-color.js';
import { mapSeriesCurveToD3 } from '../map-series-curve-to-d3.js';

const PIXEL_CORRECTION = 1;

function applyPixelCorrection(value: number, canvasSize: number): number {
  if (value === 0) {
    return PIXEL_CORRECTION;
  } else if (value === canvasSize) {
    return canvasSize - PIXEL_CORRECTION;
  }
  return value;
}

type FnLineCanvasStyle<Datum> = (
  ctx: CanvasRenderingContext2D,
  lineCanvas: LineCanvasInstance<Datum>,
  x: FnCanvasAccessor<Datum>,
  y: FnCanvasAccessor<Datum>,
) => void;
/**
 * Builds style FN Line based on parameters.
 *
 * @param strokeWidth - stroke width
 * @param enableDotOverflow - enable dots indicators to be drawn overflowing the plot area
 * @param radius - scatter plot radius
 * @returns - fn print Line canvas
 */
export function printLineBuilder<Datum>(
  strokeWidth: number,
  enableDotOverflow: boolean,
  radius?: number,
  overlayPadding: number = OVERLAY_PADDING,
): FnLineCanvasStyle<Datum> {
  const line = d3Line<Datum>();

  return (ctx, lineCanvas, x, y) => {
    const {
      color,
      data,
      opacity = DEFAULT_FILL_OPACITY,
      defined = () => true,
      displayScatterPlot,
      curve = DEFAULT_SERIES_CURVE_SHAPE,
    } = lineCanvas;

    const lineColor = getCanvasColor(color(data[0]));

    const lineCurve = mapSeriesCurveToD3(curve);

    ctx.save(); // save before applying any translation

    if (enableDotOverflow) {
      ctx.translate(overlayPadding / 2, overlayPadding / 2);
      ctx.save(); // save before doing clipping

      ctx.beginPath();
      ctx.rect(0, 0, lineCanvas.canvasWidth, lineCanvas.canvasHeight);
      ctx.clip();
    }

    ctx.beginPath();

    // Set stroke styles BEFORE path generation so that batched intermediate
    // flushes (from the batched context) render with correct appearance.
    ctx.lineJoin = 'bevel';
    ctx.lineCap = 'round';
    ctx.globalAlpha = opacity;
    ctx.lineWidth = strokeWidth;
    ctx.filter = 'none';
    ctx.strokeStyle = lineColor;

    const batchedCtx = createBatchedStrokeContext(ctx, PATH_BATCH_SIZE);

    const linePath = line
      .defined(defined)
      .curve(lineCurve)
      .x((d: Datum) => applyPixelCorrection(x(d, 0), lineCanvas.canvasWidth))
      .y((d: Datum) => applyPixelCorrection(y(d, 0), lineCanvas.canvasHeight))
      .context(batchedCtx);

    linePath(data);

    ctx.stroke();

    if (enableDotOverflow) {
      ctx.restore();
    }

    if (displayScatterPlot) {
      // Set fill styles before the loop so batched flushes use correct appearance.
      ctx.filter = 'none';
      ctx.globalAlpha = opacity;
      ctx.fillStyle = lineColor;

      ctx.beginPath();
      const dataLength = data.length;
      const scatterRadius = radius ?? DEFAULT_DATA_POINT_RADIUS;
      let arcCount = 0;

      for (let i = 0; i < dataLength; i++) {
        if (
          !defined(data[i]) ||
          !displayScatterPlot(data, i) ||
          y(data[i], i) > lineCanvas.canvasHeight ||
          y(data[i], i) < 0 ||
          x(data[i], i) > lineCanvas.canvasWidth ||
          x(data[i], i) < 0
        ) {
          continue;
        }

        ctx.moveTo(
          applyPixelCorrection(x(data[i], i), lineCanvas.canvasWidth),
          applyPixelCorrection(y(data[i], i), lineCanvas.canvasHeight),
        );
        ctx.arc(
          applyPixelCorrection(x(data[i], i), lineCanvas.canvasWidth),
          applyPixelCorrection(y(data[i], i), lineCanvas.canvasHeight),
          scatterRadius,
          0,
          2 * Math.PI,
        );

        arcCount++;
        if (arcCount >= ARC_BATCH_SIZE) {
          ctx.fill();
          ctx.beginPath();
          arcCount = 0;
        }
      }

      ctx.fill();
    }

    ctx.restore();
  };
}
